import { GpxPoint } from '../types/gpx';

interface SimplifiedPoint extends GpxPoint {
  distance: number; // Cumulative distance from the start in meters
}

/**
 * Calculates the distance between two geographic coordinates using the Haversine formula.
 * @param lat1 Latitude of the first point
 * @param lon1 Longitude of the first point
 * @param lat2 Latitude of the second point
 * @param lon2 Longitude of the second point
 * @returns Distance in meters
 */
function haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371e3; // Earth radius in meters
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δφ = (lat2 - lat1) * Math.PI / 180;
  const Δλ = (lon2 - lon1) * Math.PI / 180;

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

/**
 * Simplifies GPX track data using interval sampling and calculates cumulative distance.
 * @param points Array of GpxPoint objects
 * @param targetPoints Approximate number of points desired in the simplified data
 * @returns Array of SimplifiedPoint objects with cumulative distance
 */
export function simplifyGPXData(points: GpxPoint[], targetPoints: number = 100): SimplifiedPoint[] {
  if (!points || points.length === 0) {
    return [];
  }

  const simplified: SimplifiedPoint[] = [];
  let cumulativeDistance = 0;
  const n = points.length;

  // Always include the first point
  simplified.push({ ...points[0], distance: 0 });

  // Calculate cumulative distance for all points first
  const pointsWithDistance: SimplifiedPoint[] = [{ ...points[0], distance: 0 }];
  for (let i = 1; i < n; i++) {
    const dist = haversineDistance(
        points[i-1].lat, points[i-1].lon,
        points[i].lat, points[i].lon
    );
    cumulativeDistance += dist;
    pointsWithDistance.push({ ...points[i], distance: cumulativeDistance });
  }

  if (n <= targetPoints) {
      // If fewer points than target, return all points with calculated distance
      return pointsWithDistance;
  }

  // Perform interval sampling
  const interval = Math.max(1, Math.floor((n - 2) / (targetPoints - 2))); // Calculate interval, ensuring at least 1

  for (let i = 1; i < n - 1; i++) {
      // Include points at the calculated interval
      if (i % interval === 0) {
          simplified.push(pointsWithDistance[i]);
      }
  }

  // Always include the last point
  // Check if the last point is already added (or very close) to avoid duplicates
  const lastAdded = simplified[simplified.length - 1];
  const lastOriginal = pointsWithDistance[n-1];
  if (lastAdded.lat !== lastOriginal.lat || lastAdded.lon !== lastOriginal.lon) {
      simplified.push(lastOriginal);
  }


  // Ensure the number of points is close to the target, adjust if needed (optional refinement)
  // This basic interval sampling might not hit the exact targetPoints number perfectly.

  return simplified;
} 